/**
 * Which origins an app may load into an iframe, read from env.json (SafeAeon v1 EXT-002).
 *
 * `EmbedAllowedOrigins` is either an array of origins or one string separated by spaces or commas:
 *   "EmbedAllowedOrigins": ["https://apps.example.gov", "http://localhost:4001"]
 * Only `https:` origins are accepted, plus plain `http:` on loopback hosts for local development.
 * The page's own origin is always allowed and need not be listed.
 *
 * The pure URL checks live in `embedAllowlist.ts` (kept import-free so it can be unit-tested in Node).
 */
import { getEnvVariable } from '../../../envs';
import { collectEmbedOrigins, isAllowedEmbedUrl } from './embedAllowlist';
/**
 * Origins from env.json `EmbedAllowedOrigins`, plus the origins of `configuredUrls`: app URLs the caller already
 * trusts because env.json configures them for framing (e.g. a Flow Viewer or dashboard URL). Invalid entries are dropped.
 */
export function readEmbedAllowedOrigins(configuredUrls = []) {
    return collectEmbedOrigins(configuredUrls, getEnvVariable('EmbedAllowedOrigins') ?? getEnvVariable('embedAllowedOrigins'));
}
/**
 * True when `url` may be loaded into an iframe: its origin is this page's, one listed in env.json
 * `EmbedAllowedOrigins`, or one of `configuredUrls`. With env.json not loaded, only same-origin URLs pass.
 */
export function isEmbedUrlAllowedByEnv(url, configuredUrls = []) {
    return isAllowedEmbedUrl(url, readEmbedAllowedOrigins(configuredUrls), window.location.origin);
}
